import { cannonFormation, HUNT_BALANCE, slotMuzzle, slotToWorld } from '@kenocyber/game-domain';
import {
  PVP_MAX_HEALTH,
  PVP_MAX_HEALTH_MILLI,
  PVP_MAX_VISUAL_SCALE,
  PVP_MIN_VISUAL_SCALE,
  PVP_SHIP_RADIUS_MU,
  pvpHullScale,
} from '@kenocyber/game-domain/pvp';
import { type PvpAppearance, shipCatalogEntry } from '@kenocyber/shared';
import { spriteRotationForWorldAngle } from '../game/art-orientation';
import { cannonAssetId, cannonTextureKey } from '../game/cannon-art';
import { PVP_MU_PER_UNIT } from './scene-model';

/**
 * How a duelling ship is drawn, as arithmetic.
 *
 * Everything here is a pure function of the SERVER's profile and the SERVER's
 * numbers. No Phaser, no canvas, no inventory: the scene maps these outputs to
 * sprites and nothing else decides what a ship looks like.
 *
 * ## Reused, not re-derived
 *
 * The hull texture comes from `shipCatalogEntry`, the barrel texture from
 * `cannon-art.ts` and the hardpoints from `cannonFormation` - the same three
 * the hunt draws from. A duel that computed its own mount points would drift
 * from the hunt the first time either moved, and a player would notice their
 * own ship changing shape between modes.
 */

/**
 * The hull the formation was measured against, in its own art units.
 *
 * `HUNT_BALANCE.formation` places hardpoints at 58, 16 and -26 forward and up
 * to 67 off the axis. Those are units of a 96-unit hunt hull. A duel's hull is
 * 52 world units across, so applying them raw would hang the wingtips 67 units
 * off a ship 26 units in radius - a battery floating in space beside its ship.
 *
 * The formation is therefore scaled BY THE HULL, which is the only reading that
 * keeps "the original mount points" true across two different world scales: the
 * wing stays in exactly the same proportion to the ship it is bolted to.
 */
const HUNT_HULL_ART_UNITS = HUNT_BALANCE.ship.displayWidth;

/** A duel hull's diameter in world units - 52, from the authoritative radius. */
export const PVP_HULL_WORLD_UNITS = (PVP_SHIP_RADIUS_MU / PVP_MU_PER_UNIT) * 2;

/** What every hunt-side length is multiplied by to land on a duel hull. */
export const PVP_FORMATION_SCALE = PVP_HULL_WORLD_UNITS / HUNT_HULL_ART_UNITS;

/** The hull's texture key and asset id, straight from the catalogue. */
export function pvpShipArt(appearance: PvpAppearance): { key: string; assetId: string } {
  const entry = shipCatalogEntry(appearance.ship);
  return { key: entry.assetId, assetId: entry.assetId };
}

/**
 * The barrel's texture key, or `null` when there is nothing to draw.
 *
 * `null` covers both a ship with no cannon and Prismatic, which is designed and
 * has no art. Neither gets a stand-in from another rarity: a player must never
 * be shown a gun that is not the one being fired at them.
 */
export function pvpCannonArt(appearance: PvpAppearance): { key: string; assetId: string } | null {
  if (appearance.cannon === null || appearance.level === 0) {
    return null;
  }
  const assetId = cannonAssetId(appearance.cannon);
  if (assetId === null) {
    return null;
  }
  return { key: cannonTextureKey(appearance.cannon), assetId };
}

export interface PvpMount {
  /** Offset from the hull centre along the hull's nose, in world units. */
  readonly forward: number;
  /** Offset to the hull's right. A mirrored pair shares `forward`. */
  readonly lateral: number;
}

/**
 * Where the barrels sit on the hull, in hull-local world units.
 *
 * The LEVEL is what selects the formation, which is why the profile carries a
 * level rather than a barrel count - see `pvp/appearance.ts`. The count is what
 * comes out of this, never what goes into it.
 */
export function pvpCannonMounts(appearance: PvpAppearance): readonly PvpMount[] {
  if (appearance.cannon === null || appearance.level === 0) {
    return [];
  }
  return cannonFormation(appearance.level).map((slot) => ({
    forward: slot.forward * PVP_FORMATION_SCALE,
    lateral: slot.lateral * PVP_FORMATION_SCALE,
  }));
}

/** The tip of each barrel, in the same hull-local units. */
export function pvpCannonMuzzles(appearance: PvpAppearance): readonly PvpMount[] {
  if (appearance.cannon === null || appearance.level === 0) {
    return [];
  }
  return cannonFormation(appearance.level)
    .map(slotMuzzle)
    .map((slot) => ({
      forward: slot.forward * PVP_FORMATION_SCALE,
      lateral: slot.lateral * PVP_FORMATION_SCALE,
    }));
}

/**
 * A hull-local mount placed in the world, for a battery pointing at `angle`.
 *
 * The angle is the BATTERY's, which is the aim - see
 * {@link pvpCannonPivotAngle} for why it is not the hull's heading.
 */
export function pvpMountInWorld(
  mount: PvpMount,
  shipX: number,
  shipY: number,
  angle: number,
  scale: number,
): { x: number; y: number } {
  return slotToWorld(
    { forward: mount.forward * scale, lateral: mount.lateral * scale },
    shipX,
    shipY,
    angle,
  );
}

/**
 * The angle the whole cannon ASSEMBLY is rotated by: the aim.
 *
 * ## The defect this exists to make impossible again
 *
 * The mounts used to be placed with the HULL's heading while each barrel sprite
 * was rotated to the AIM about its own centre. The two angles are independent
 * in a duel - the hull turns towards where it is travelling, the guns towards
 * the cursor - so as soon as they differed the battery stopped being a battery:
 * a barrel sat at a hardpoint the hull had put somewhere and pointed somewhere
 * else, spinning on the spot. A player looking at it described exactly that,
 * and a capture at sixteen fixed aims proved it: the sprite's screen position
 * never moved through the whole sweep while its rotation went all the way
 * round.
 *
 * ## Why the aim, and why the whole assembly
 *
 * Because a battery is rigid. The hunt has always drawn it that way - one
 * rotation for the hull and every barrel, `HuntScene` calling `setRotation` on
 * each cannon with the ship's own angle - and it reads as installed hardware
 * because the mount and the muzzle turn together. A duel differs in ONE thing:
 * which angle the assembly follows. It is the aim there, because the hull is
 * free to fly one way and shoot another.
 *
 * So the mount offset is rotated by this angle and the sprite is rotated by
 * this angle, which makes the pivot the SHIP'S CENTRE: the hardpoint sweeps a
 * circle of radius `|mount| * scale` about the hull as the cursor moves, and
 * the barrel keeps the same attitude relative to its own mount the whole way
 * round. That is the "rotating about the ship's central axis" the report asked
 * for, and it costs one argument rather than a second geometry.
 *
 * ## The fallback
 *
 * `null` aim means the room has not said where this participant is pointing -
 * a frame that arrives before the first intent. The hull's heading is the only
 * other angle that exists, and using it keeps the battery attached rather than
 * snapping it to due east.
 */
export function pvpCannonPivotAngle(aim: number | null, hullHeading: number): number {
  return aim ?? hullHeading;
}

/**
 * The rotation the barrel SPRITE is drawn at, for a given pivot angle.
 *
 * Separate from {@link pvpCannonPivotAngle} because they are different
 * quantities that happened to share a number. The pivot is a WORLD angle: it
 * places the mount through `slotToWorld`, and it must stay exactly what it was.
 * This is a SPRITE rotation, and the art is drawn facing up.
 */
export function pvpCannonSpriteRotation(pivotAngle: number): number {
  return spriteRotationForWorldAngle(pivotAngle);
}

/**
 * How large a ship is DRAWN, from the maximum health the server gave it.
 *
 * ## Uma casca sobre a curva canónica, e nada mais (gate V28G.R)
 *
 * A curva vive em `game-domain/hull/scale.ts` e é a MESMA que decide a hitbox,
 * o raio autoritativo, a órbita das serras, o ponto onde um tiro nasce e as
 * paredes. Esta função existe para os chamadores que só têm um número de vida à
 * mão; ela não tem aritmética própria, e é essa ausência que impede a aparência
 * de crescer sem a física.
 *
 * ## Porque a curva é LINEAR
 *
 * Ela era a raiz quadrada - a resposta clássica, com a vida a ler-se como área
 * - e foi medida numa bancada com as duas naves lado a lado: `sqrt(1,6)` são
 * `+26,5 %` de largura, dez pixels no zoom padrão, e a diferença não se lia. O
 * dono trocou-a pela proporção direta, que é a mesma promessa que a etiqueta
 * faz: `+60 % de vida máxima` e `+60 %` de nave.
 *
 * ## E porque já não há tecto
 *
 * O `clamp` de {@link PVP_MAX_VISUAL_SCALE} foi REMOVIDO por decisão do dono no
 * gate V28G.R: ele tornaria o Coração do Titã invisível para qualquer casco que
 * já lá estivesse, e um teto sobre o que um item concede é o que o `CLAUDE.md`
 * proíbe. A escala é monotónica - mais vida máxima é sempre uma nave maior.
 *
 * O piso de 1,0 fica, e nunca morde: nenhuma fonte legal leva a vida máxima
 * abaixo de {@link PVP_MAX_HEALTH}, e um casco no seu limite ainda é uma nave
 * que alguém tem de apontar.
 *
 * ## O que ela é agora, e não era
 *
 * **É a hitbox.** `pvpHullRadiusMu` sai da mesma escala, e a simulação colide
 * por ele: uma nave desenhada maior é um alvo maior, é travada mais cedo pelas
 * paredes, e os tiros dela nascem mais longe do centro. A assimetria que este
 * comentário descrevia - "a ship drawn at three times its art still collides as
 * one" - acabou, e era o defeito que o gate V28G.R existiu para corrigir.
 *
 * It is not CURRENT health either. A ship does not shrink as it is hit, which
 * would make a losing player harder to hit; the input is the authoritative
 * maximum, which is fixed for the duel.
 */
/**
 * O teto de um casco, lido de um perfil do FIO (gate V28G.S.1).
 *
 * O quadro transporta `maxHealthMilli` como string decimal, e a geometria
 * canónica pede um `bigint`. A travessia acontece aqui e só aqui: um `BigInt()`
 * espalhado por cada sítio que desenha seria um sítio a mais onde alguém o
 * esquece e volta a um `number`.
 *
 * Uma cadeira sem perfil - um `welcome` que ainda não chegou - mede a nave de
 * referência. Isso é "não sei", e não "é pequena".
 */
export function pvpHullOf(profile: { readonly maxHealthMilli: string } | undefined): {
  readonly maxHealthMilli: bigint;
} {
  return {
    maxHealthMilli: profile === undefined ? PVP_MAX_HEALTH_MILLI : BigInt(profile.maxHealthMilli),
  };
}

export function pvpVisualScale(maxHealthMilli: bigint): number {
  if (maxHealthMilli <= 0n) {
    return PVP_MIN_VISUAL_SCALE;
  }
  return pvpHullScale({ maxHealthMilli });
}

/**
 * The scale to hand a Phaser image so it covers the hull, at `visualScale`.
 *
 * The texture's own pixel height is the divisor, exactly as `HuntScene` does
 * it, so art delivered at a different resolution lands at the same world size.
 */
export function pvpSpriteScale(
  textureHeight: number,
  worldUnits: number,
  visualScale: number,
): number {
  if (!Number.isFinite(textureHeight) || textureHeight <= 0) {
    return visualScale;
  }
  return (worldUnits * visualScale) / textureHeight;
}

/** The cannon sprite's world size, in the same proportion the hunt draws it. */
export const PVP_CANNON_WORLD_UNITS = HUNT_BALANCE.ship.cannonDisplaySize * PVP_FORMATION_SCALE;
